import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import HPO_RAW_DATA from 'src/constants/hpo.json'

const apiDataFormatters = {
  sample: (sample) => {
    const result = {
      id: sample.file_id,
      name: sample.sample_name,
      // Additional file information for display
      filename: sample.filename,
      createdAt: sample.created_at,
      analysisType: sample.analysis_type,
      // TODO: We shouldn't need this just for the sample name
      // refactor SampleFilter.jsx
      originalData: sample,
    }

    return result
  },

  disease: (disease) => ({
    id: disease.omim,
    name: disease.disease,
    omim: disease.omim,
  }),

  hpo: ([id, name]) => ({
    id: id,
    name: name,
  }),

  panel: ({ id, name, organisation, predefined }) => ({
    id: id,
    name: name,
  }),
}

const fetchAllAnalysis = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/analysis/list`)
  return data
}

const commonService = {
  /**
   * Fetch all samples using the new analysis API
   * @param {Object} contextFunctions - Functions from ChatbotContext
   * @returns {Promise} Promise with samples data
   */
  fetchAllSamples: async (contextFunctions = null) => {
    if (contextFunctions) {
      const { getCachedData, updateCache } = contextFunctions
      const cachedSamples = getCachedData('sample')

      if (cachedSamples) {
        return cachedSamples
      }

      // Cache miss, fetch from new API
      // We want the results field object from the data not the pagination
      const { results: analysisData } = await fetchAllAnalysis()

      // Filter and format the data
      const filteredData = analysisData.filter((analysis) => {
        return analysis.status === 'DONE'
      })

      const mappedData = filteredData
        .map((analysis) => {
          return {
            sample_name: analysis.sample_name,
            filename: analysis.file_names[0],
            file_id: analysis.file_id,
            analysis_name: analysis.analysis_name,
            analysis_id: analysis.analysis_id,
            analysis_type: analysis.analysis_type,
            created_at: analysis.created_at,
          }
        })
        .map(apiDataFormatters.sample)

      updateCache('sample', mappedData)
      return mappedData
    }

    const { results: analysisData } = await fetchAllAnalysis()

    // Filter and format the data
    const filteredData = analysisData.filter((analysis) => {
      return analysis.status === 'DONE'
    })

    const mappedData = filteredData
      .map((analysis) => {
        return {
          sample_name: analysis.sample_name,
          filename: analysis.file_names[0],
          file_id: analysis.file_id,
          analysis_name: analysis.analysis_name,
          analysis_id: analysis.analysis_id,
          analysis_type: analysis.analysis_type,
          created_at: analysis.created_at,
        }
      })
      .map(apiDataFormatters.sample)

    return mappedData
  },

  /**
   * Fetch diseases from the OMIM API
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search term
   * @param {Object} contextFunctions - Functions from ChatbotContext
   * @returns {Promise} Promise with diseases data
   */
  fetchDiseases: async (params = {}) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/omim-diseases/`, {
        params: {
          ...params,
          limit: 100,
        },
      })
      const diseases = response.data.results.map(apiDataFormatters.disease)
      const seen = new Set()
      return diseases.filter((disease) => {
        const key = disease.id ?? disease.name
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    } catch (e) {
      console.error('Error fetching diseases (on-demand):', e)
      return []
    }

    // if (params.search) {
    //   const response = await axios.get(`${API_BASE_URL}/omim-diseases/`, {
    //     params: {
    //       ...params,
    //       limit: 100,
    //     },
    //   })
    //   const diseases = response.data.results.map(apiDataFormatters.disease)

    // Update cache with new entries
    //   const { getCachedData, updateCache } = contextFunctions
    //   const cachedDiseases = getCachedData('disease') || []

    //   if (diseases.length > 0) {
    //     const existingIds = new Set(cachedDiseases.map((disease) => disease.id))

    //     const newDiseases = diseases.filter((disease) => !existingIds.has(disease.id))

    //     if (newDiseases.length > 0) {
    //       const mergedDiseases = [...cachedDiseases, ...newDiseases]
    //       updateCache('disease', mergedDiseases)
    //     }
    //   }

    //   return diseases
    // }

    // // No search term, check cache first
    // const { getCachedData, updateCache } = contextFunctions
    // const cachedDiseases = getCachedData('disease')
    // if (cachedDiseases && cachedDiseases.length > 0) {
    //   return cachedDiseases
    // }

    // // Cache miss or empty cache, fetch all diseases with pagination
    // const allDiseases = await commonService.fetchAllDiseasesWithPagination()
    // updateCache('disease', allDiseases)
    // return allDiseases
  },

  // fetchAllDiseases: async (contextFunctions = null) => {
  // // If no context functions provided, just fetch without caching
  // if (!contextFunctions) {
  //   const allDiseases = await commonService.fetchAllDiseasesWithPagination()
  //   return allDiseases
  // }
  // // Check cache first
  // const { getCachedData, updateCache } = contextFunctions
  // const cachedDiseases = getCachedData('disease')
  // if (cachedDiseases) {
  //   return cachedDiseases
  // }
  // const allDiseases = await commonService.fetchAllDiseasesWithPagination()
  // updateCache('disease', allDiseases)
  // return allDiseases
  // },

  // Helper function to fetch all diseases with pagination
  // fetchAllDiseasesWithPagination: async () => {
  // let allDiseases = []
  // let nextUrl = `${API_BASE_URL}/omim-diseases/?limit=1000`
  // // Keep fetching until there's no next page!
  // while (nextUrl) {
  //   const response = await axios.get(nextUrl)
  //   const pageDiseases = response.data.results.map(apiDataFormatters.disease)
  //   allDiseases = [...allDiseases, ...pageDiseases]
  //   nextUrl = response.data.next
  // }
  // return allDiseases
  // },

  /**
   * Fetch HPO terms
   * @param {Object} contextFunctions - Functions from ChatbotContext
   * @returns {Promise} Promise with HPO terms data
   */
  fetchHPOTerms: async (contextFunctions = null) => {
    // Check cache first if context is provided
    if (contextFunctions) {
      const { getCachedData, updateCache } = contextFunctions
      const cachedHPO = getCachedData('hpo')
      if (cachedHPO) {
        return cachedHPO
      }

      // Cache miss
      const hpoTerms = HPO_RAW_DATA.map(apiDataFormatters.hpo)
      updateCache('hpo', hpoTerms)
      return hpoTerms
    }

    // No context provided, just fetch from API
    const hpoTerms = HPO_RAW_DATA.map(apiDataFormatters.hpo)
    return hpoTerms
  },

  /**
   * Fetch all gene sets
   * @param {Object} contextFunctions - Functions from ChatbotContext
   * @returns {Promise} Promise with gene sets data
   */
  fetchPanels: async (contextFunctions = null) => {
    // Check cache first if context is provided
    if (contextFunctions) {
      const { getCachedData, updateCache } = contextFunctions
      const cachedPanels = getCachedData('panel')
      if (cachedPanels) {
        return cachedPanels
      }

      // Cache miss, fetch from API
      const response = await axios.get(`${API_BASE_URL}/genesets/get_all_genesets/`)
      const formattedData = response.data.results.map(apiDataFormatters.panel)
      updateCache('panel', formattedData)
      return formattedData
    }

    const response = await axios.get(`${API_BASE_URL}/genesets/get_all_genesets/`)
    return response.data.results.map(apiDataFormatters.panel)
  },

  /**
   * Download a report file by VCF ID
   * @param {string} vcfId - The VCF ID for downloading the report
   * @param {string} sampleName - The sample name for the file
   * @returns {Promise} Promise with blob data
   */
  downloadReport: async (vcfId, sampleName) => {
    // Clean up sample name for file naming
    sampleName = sampleName.replace('.gz', '').replace('.vcf', '').replace('.raw', '')

    // Add report extension
    const fileName = `${sampleName}_report.docx`

    try {
      const response = await axios.get(`${API_BASE_URL}/vcf/download-report/${vcfId}`, {
        responseType: 'blob',
      })

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]))

      // Create a temporary anchor element
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url

      // Set the file name
      a.download = fileName

      // Append to the body and click
      document.body.appendChild(a)
      a.click()

      // Clean up
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      return true
    } catch (error) {
      console.error('Error downloading report:', error)
      throw error
    }
  },
}

export default commonService
